import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import Select, { InputActionMeta, SingleValue } from 'react-select'
import { searchPlatformFieldsAsync } from 'src/redux/actions/productActions'
import { useAppDispatch } from 'src/redux/storeHooks'
import { withSavedSelectOption } from './utils/miscMappingHydrate'
import {
  getListingProfileReactSelectMenuPortalTarget,
  listingProfileMoreMappingSelectStyles,
} from './utils/listingProfileFormPlatformUtils'

export type ListingProfileFieldType = 'NATIVE' | 'CUSTOM'

type FieldOption = { value: number; label: string }

type Props = {
  name?: string
  platformId?: number | null
  fieldType: ListingProfileFieldType
  value: unknown
  /** Label for a saved id that is not on the current result page. */
  selectedLabel?: string | null
  placeholder: string
  disabled?: boolean
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  className?: string
}

const PAGE_SIZE = 100
const SEARCH_DEBOUNCE_MS = 400
const MENU_Z_INDEX = 10000

const valueToString = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const text = String(value)
  if (text === 'NaN' || text === 'undefined') return ''
  return text
}

const rowsFromPayload = (payload: unknown): { id: number; display_name?: string; api_name?: string }[] => {
  if (Array.isArray(payload)) return payload
  if (payload && typeof payload === 'object' && Array.isArray((payload as { results?: unknown }).results)) {
    return (payload as { results: { id: number; display_name?: string; api_name?: string }[] }).results
  }
  return []
}

export const ListingProfileFieldSearchSelect: FC<Props> = ({
  name,
  platformId,
  fieldType,
  value,
  selectedLabel = null,
  placeholder,
  disabled = false,
  onChange,
}) => {
  const dispatch = useAppDispatch()
  const [menuOpen, setMenuOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<FieldOption[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [picked, setPicked] = useState<FieldOption | null>(null)
  const requestId = useRef(0)

  const valueStr = valueToString(value)

  const loadPage = async (nextPage: number, search: string, replace: boolean) => {
    if (!platformId) return
    const currentRequest = ++requestId.current
    setLoading(true)
    try {
      const response: any = await dispatch(
        searchPlatformFieldsAsync({
          platformId,
          otherParams: {
            field_type: fieldType,
            owner: 'PRODUCT',
            includeLocalFields: fieldType === 'NATIVE',
            search_string: search.trim(),
            page: nextPage,
            limit: PAGE_SIZE,
          },
        })
      )
      if (currentRequest !== requestId.current) return
      const rows = rowsFromPayload(response?.payload).map((field) => ({
        value: Number(field.id),
        label: field.display_name || field.api_name || String(field.id),
      }))
      setOptions((prev) => {
        const base = replace ? [] : prev
        const seen = new Set(base.map((opt) => opt.value))
        return [...base, ...rows.filter((opt) => !seen.has(opt.value))]
      })
      setPage(nextPage)
      setHasMore(rows.length >= PAGE_SIZE)
    } finally {
      if (currentRequest === requestId.current) setLoading(false)
    }
  }

  useEffect(() => {
    if (!menuOpen || !platformId) return
    const handle = window.setTimeout(() => {
      void loadPage(1, query, true)
    }, query.trim() ? SEARCH_DEBOUNCE_MS : 0)
    return () => window.clearTimeout(handle)
    // loadPage closes over the latest dispatch and field type for this menu session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen, query, platformId, fieldType])

  const savedOption = useMemo(() => {
    const id = Number(valueStr)
    if (!valueStr || !Number.isFinite(id)) return null
    const label =
      (picked && String(picked.value) === valueStr ? picked.label : null) ??
      (selectedLabel?.trim() || null)
    if (!label) return null
    return { value: id, label }
  }, [picked, selectedLabel, valueStr])

  const displayOptions = withSavedSelectOption(options, savedOption, valueStr)
  const selected = displayOptions.find((opt) => String(opt.value) === valueStr) ?? null

  const emitChange = (nextValue: string, label: string | null) => {
    if (nextValue && label) setPicked({ value: Number(nextValue), label })
    if (!nextValue) setPicked(null)
    onChange({
      target: { name: name ?? '', value: nextValue },
    } as React.ChangeEvent<HTMLSelectElement>)
  }

  const handleInputChange = (next: string, meta: InputActionMeta) => {
    if (meta.action !== 'input-change') return
    setQuery(next)
  }

  const handleChange = (next: SingleValue<FieldOption>) => {
    if (!next) {
      emitChange('', null)
      return
    }
    emitChange(String(next.value), next.label)
  }

  return (
    <div className="w-100">
      <Select<FieldOption, false>
        className="w-100"
        classNamePrefix="listing-profile-form-select"
        inputId={name}
        name={name}
        isSearchable
        isClearable
        isLoading={loading}
        isDisabled={disabled || !platformId}
        placeholder={placeholder}
        options={displayOptions}
        value={selected}
        filterOption={() => true}
        menuIsOpen={menuOpen}
        menuPlacement="auto"
        menuPosition="fixed"
        menuPortalTarget={getListingProfileReactSelectMenuPortalTarget()}
        menuShouldScrollIntoView={false}
        onMenuOpen={() => setMenuOpen(true)}
        onMenuClose={() => {
          setMenuOpen(false)
          setQuery('')
        }}
        onInputChange={handleInputChange}
        onMenuScrollToBottom={() => {
          if (hasMore && !loading) void loadPage(page + 1, query, false)
        }}
        onChange={handleChange}
        noOptionsMessage={() => 'No matches'}
        styles={{
          ...listingProfileMoreMappingSelectStyles,
          container: (base) => ({ ...base, width: '100%' }),
          control: (base) => ({ ...base, width: '100%' }),
          menuPortal: (base) => ({ ...base, zIndex: MENU_Z_INDEX }),
          menu: (base) => ({
            ...base,
            marginTop: 4,
            marginBottom: 4,
            border: '1px solid #d8dde6',
            borderRadius: '0.5rem',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.16)',
            overflow: 'hidden',
          }),
          menuList: (base) => ({
            ...base,
            maxHeight: '16rem',
            paddingTop: '0.25rem',
            paddingBottom: '0.25rem',
          }),
          option: (base, state) => ({
            ...base,
            padding: '0.8125rem 0.85rem',
            fontSize: '0.875rem',
            fontWeight: 400,
            lineHeight: 1.35,
            color: '#181c32',
            cursor: 'pointer',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            backgroundColor: state.isFocused || state.isSelected ? '#deebff' : '#fff',
            ':active': { backgroundColor: '#deebff' },
          }),
        }}
      />
    </div>
  )
}

export default ListingProfileFieldSearchSelect
